import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { examsService } from '@/services/exams.service';
import { Save, X } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';

export function ExamForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Static dropdown configurations (these could be fetched from adminService if endpoints exist)
  const certifyingBodies = [
    { value: 'ANBIMA', label: 'ANBIMA', is_active: true, order_index: 1 },
    { value: 'CFC', label: 'CFC', is_active: true, order_index: 2 },
    { value: 'CVM', label: 'CVM', is_active: true, order_index: 3 },
    { value: 'IBGC', label: 'IBGC', is_active: true, order_index: 4 },
    { value: 'Other', label: 'Other', is_active: true, order_index: 5 },
  ];
  const examStatuses = [
    { value: 'active', label: 'Active', label_en: 'Active', is_active: true, order_index: 1 },
    { value: 'legacy', label: 'Legacy', label_en: 'Legacy', is_active: true, order_index: 2 },
    { value: 'inactive', label: 'Inactive', label_en: 'Inactive', is_active: true, order_index: 3 },
    { value: 'upcoming', label: 'Upcoming', label_en: 'Upcoming', is_active: true, order_index: 4 },
  ];
  const languages = [
    { value: 'pt-BR', label: 'Português (BR)', label_en: 'Portuguese (BR)', is_active: true, order_index: 1 },
    { value: 'en-US', label: 'English (US)', label_en: 'English (US)', is_active: true, order_index: 2 },
    { value: 'es-ES', label: 'Español (ES)', label_en: 'Spanish (ES)', is_active: true, order_index: 3 },
  ];
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    exam_id: '',
    name: '',
    name_en: '',
    certifying_body: 'ANBIMA',
    exam_status: 'active' as 'active' | 'legacy' | 'inactive' | 'upcoming',
    total_questions: 50,
    duration_minutes: 150,
    passing_score_percent: 70,
    calculator_required: false,
    language: 'pt-BR',
  });

  const { data: exam } = useQuery({
    queryKey: ['exam', id],
    queryFn: async () => {
      try {
        return await examsService.getExamById(id!);
      } catch (error) {
        console.error('Failed to fetch exam:', error);
        return null;
      }
    },
    enabled: isEditing,
  });

  useEffect(() => {
    if (exam) {
      setFormData({
        exam_id: exam.exam_id,
        name: exam.name,
        name_en: exam.name_en || '',
        certifying_body: exam.certifying_body,
        exam_status: exam.exam_status,
        total_questions: exam.total_questions,
        duration_minutes: exam.duration_minutes,
        passing_score_percent: exam.passing_score_percent,
        calculator_required: exam.calculator_required,
        language: exam.language,
      });
    }
  }, [exam]);

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await examsService.createExam(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['exams'] });
      showToast('success', 'Exam Created', `Exam "${data.name}" has been created successfully.`);
      navigate('/exams');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to create exam';
      showToast('error', 'Creation Failed', message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await examsService.updateExam(id!, data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['exams'] });
      queryClient.invalidateQueries({ queryKey: ['exam', id] });
      showToast('success', 'Exam Updated', `Exam "${data.name}" has been updated successfully.`);
      navigate('/exams');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to update exam';
      showToast('error', 'Update Failed', message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Edit Exam' : 'Create Exam'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-6">
        {/* Basic Information */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Exam ID *
              </label>
              <input
                type="text"
                required
                value={formData.exam_id}
                onChange={(e) => handleChange('exam_id', e.target.value)}
                disabled={isEditing}
                className="input-field"
                placeholder="CPA"
              />
              {isEditing && (
                <p className="text-xs text-gray-500 mt-1">Exam ID cannot be changed</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Certifying Body *
              </label>
              <select
                required
                value={formData.certifying_body}
                onChange={(e) => handleChange('certifying_body', e.target.value)}
                className="input-field"
              >
                {certifyingBodies?.filter(b => b.is_active).sort((a, b) => a.order_index - b.order_index).map(body => (
                  <option key={body.value} value={body.value}>
                    {body.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Manage certifying bodies in System Settings → Dropdown Config
              </p>
            </div>
          </div>
        </div>

        {/* Exam Details */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Exam Details</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Exam Name (Portuguese) *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="input-field"
                placeholder="Certificação Profissional ANBIMA..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Exam Name (English)
              </label>
              <input
                type="text"
                value={formData.name_en}
                onChange={(e) => handleChange('name_en', e.target.value)}
                className="input-field"
                placeholder="ANBIMA Professional Certification..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status *
                </label>
                <select
                  required
                  value={formData.exam_status}
                  onChange={(e) => handleChange('exam_status', e.target.value)}
                  className="input-field"
                >
                  {examStatuses?.filter(s => s.is_active).sort((a, b) => a.order_index - b.order_index).map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label_en || status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Language *
                </label>
                <select
                  required
                  value={formData.language}
                  onChange={(e) => handleChange('language', e.target.value)}
                  className="input-field"
                >
                  {languages?.filter(l => l.is_active).sort((a, b) => a.order_index - b.order_index).map(lang => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label_en || lang.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Exam Configuration */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Exam Configuration</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Questions *
              </label>
              <input
                type="number"
                required
                min="1"
                value={formData.total_questions}
                onChange={(e) => handleChange('total_questions', parseInt(e.target.value))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (minutes) *
              </label>
              <input
                type="number"
                required
                min="1"
                value={formData.duration_minutes}
                onChange={(e) => handleChange('duration_minutes', parseInt(e.target.value))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Passing Score (%) *
              </label>
              <input
                type="number"
                required
                min="0"
                max="100"
                value={formData.passing_score_percent}
                onChange={(e) => handleChange('passing_score_percent', parseInt(e.target.value))}
                className="input-field"
              />
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.calculator_required}
                onChange={(e) => handleChange('calculator_required', e.target.checked)}
                className="w-4 h-4 text-primary-600"
              />
              <label className="ml-2 text-sm text-gray-700">
                Calculator Required
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate('/exams')}
            className="btn-secondary flex items-center"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="btn-primary flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {isPending ? 'Saving...' : isEditing ? 'Update Exam' : 'Create Exam'}
          </button>
        </div>
      </form>
    </div>
  );
}
